/**
 * Date/number formatting in the VISITOR's timezone (règle 4). Every function
 * accepts an explicit `timeZone` so tests can pin any zone deterministically.
 */

import { localeTag, lang, t } from "./i18n.ts";

export interface TzOptions {
  readonly timeZone?: string;
  readonly locale?: string;
}

/** "2026-06-13" in the given (or local) timezone — used for day grouping. */
export function localDayKey(iso: string, opts: TzOptions = {}): string {
  return new Intl.DateTimeFormat("en-CA", {
    ...(opts.timeZone ? { timeZone: opts.timeZone } : {}),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));
}

export function isSameLocalDay(isoA: string, isoB: string, opts: TzOptions = {}): boolean {
  return localDayKey(isoA, opts) === localDayKey(isoB, opts);
}

/** Local hour 0–23 in the given timezone. */
export function localHour(iso: string, opts: TzOptions = {}): number {
  return Number(
    new Intl.DateTimeFormat("en-GB", {
      ...(opts.timeZone ? { timeZone: opts.timeZone } : {}),
      hour: "2-digit",
      hour12: false,
    }).format(new Date(iso)),
  );
}

/** End hour (local) of the extended match-day window: tomorrow's matches that
 *  kick off before this hour still belong to "today" (WC night matches in the
 *  Americas spill into the next morning for other timezones). */
export const TODAY_BAND_NEXT_DAY_UNTIL_HOUR = 9;

/**
 * Does a match belong to the visitor's "today" band? True when its LOCAL
 * kickoff day is today, OR it is tomorrow but kicks off before 09:00 local
 * (decision 13/06/2026). Timezone-explicit for deterministic tests.
 */
export function isInTodayBand(matchIso: string, nowIso: string, opts: TzOptions = {}): boolean {
  const todayKey = localDayKey(nowIso, opts);
  const tomorrowKey = localDayKey(new Date(Date.parse(nowIso) + 86_400_000).toISOString(), opts);
  const matchKey = localDayKey(matchIso, opts);
  if (matchKey === todayKey) return true;
  if (matchKey === tomorrowKey) return localHour(matchIso, opts) < TODAY_BAND_NEXT_DAY_UNTIL_HOUR;
  return false;
}

/** "20:00" local time. */
export function fmtTime(iso: string, opts: TzOptions = {}): string {
  return new Intl.DateTimeFormat(opts.locale ?? localeTag(), {
    ...(opts.timeZone ? { timeZone: opts.timeZone } : {}),
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

/** "sam. 13 juin, 20:00" local. */
export function fmtKick(iso: string, opts: TzOptions = {}): string {
  return new Intl.DateTimeFormat(opts.locale ?? localeTag(), {
    ...(opts.timeZone ? { timeZone: opts.timeZone } : {}),
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

/** "Samedi 13 juin" local — en-tête de jour (navigation des matchs passés). */
export function fmtDayLabel(iso: string, opts: TzOptions = {}): string {
  const s = new Intl.DateTimeFormat(opts.locale ?? localeTag(), {
    ...(opts.timeZone ? { timeZone: opts.timeZone } : {}),
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(iso));
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** "13/06 20:31" local — freshness banner. */
export function fmtShortDateTime(iso: string, opts: TzOptions = {}): string {
  return new Intl.DateTimeFormat(opts.locale ?? localeTag(), {
    ...(opts.timeZone ? { timeZone: opts.timeZone } : {}),
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

/** "il y a 42 s" / "{n} min ago" / "hace {n} h" — selon la langue active. */
export function fmtAgo(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return t("ago.s", { n: s });
  const m = Math.floor(s / 60);
  if (m < 60) return t("ago.min", { n: m });
  return t("ago.h", { n: Math.floor(m / 60) });
}

/** Au-delà de ce délai APRÈS le coup d'envoi sans données live, on cesse
 *  d'afficher « imminent » : le flux a manifestement du retard sur ce match. */
export const COUNTDOWN_STALE_MS = 12 * 60_000;

/**
 * Compte à rebours vers un coup d'envoi : "dans 2 j 4 h" / "dans 3 h 12 min" /
 * "dans 12 min 03 s" / "dans 45 s". Coup d'envoi juste atteint (flux qui n'a pas
 * encore basculé) → "imminent" ; mais s'il est dépassé de plus de
 * COUNTDOWN_STALE_MS sans données, on l'avoue → "en attente du direct".
 */
export function fmtCountdown(ms: number): string {
  if (ms <= 0) return t(ms < -COUNTDOWN_STALE_MS ? "cd.waiting" : "cd.imminent");
  const total = Math.round(ms / 1000);
  const d = Math.floor(total / 86_400);
  const h = Math.floor((total % 86_400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number): string => String(n).padStart(2, "0");
  if (d > 0) return t("cd.days", { d, h });
  if (h > 0) return t("cd.hours", { h, m: pad(m) });
  if (m > 0) return t("cd.mins", { m, s: pad(s) });
  return t("cd.secs", { s });
}

/** Freshness traffic light from the age of live.json (generatedAt). */
export type Freshness = "fresh" | "stale" | "dead";
export const FRESH_MAX_MS = 2 * 60_000;
export const STALE_MAX_MS = 6 * 60_000;
export function freshness(ageMs: number): Freshness {
  if (ageMs <= FRESH_MAX_MS) return "fresh";
  if (ageMs <= STALE_MAX_MS) return "stale";
  return "dead";
}

/** Pourcentage localisé : "25.8%" (en) / "25,8 %" (fr, es). */
export function fmtPct(p: number, digits = 1): string {
  const num = (100 * p).toFixed(digits);
  return lang() === "en" ? `${num}%` : `${num.replace(".", ",")} %`;
}

export function fmtSigned(n: number): string {
  return n > 0 ? `+${n}` : String(n);
}
